import type { useGT } from 'gt-react';
import type { CellViolationDetail } from '@erp/item-master-rules';

type Gt = ReturnType<typeof useGT>;

type BlankCellDetail = Extract<
  CellViolationDetail,
  { rule: 'slash-count' | 'whitespace-only' }
>;

export function isBlankCellDetail(
  detail: CellViolationDetail,
): detail is BlankCellDetail {
  return detail.rule === 'slash-count' || detail.rule === 'whitespace-only';
}

export function describeBlankCellWarning(
  gt: Gt,
  detail: CellViolationDetail,
  column: string,
  value: string,
): string | null {
  if (detail.rule === 'slash-count') {
    return describeSlashCountWarning(gt, detail, column, value);
  }
  if (detail.rule === 'whitespace-only') {
    return describeWhitespaceOnlyWarning(gt, column, value);
  }
  return null;
}

function describeSlashCountWarning(
  gt: Gt,
  detail: Extract<BlankCellDetail, { rule: 'slash-count' }>,
  column: string,
  value: string,
): string {
  return gt(
    '{column} {value} has {actual} slashes; clearing this field takes exactly {expected}.',
    {
      column,
      value,
      actual: detail.actual,
      expected: detail.expected,
      context:
        'review grid validation warning: a slash-only value is the wrong length for this AS/400 blank-out marker',
    },
  );
}

function describeWhitespaceOnlyWarning(
  gt: Gt,
  column: string,
  value: string,
): string {
  return gt(
    '{column} {value} is only spaces or tabs. That is probably a mistake; a blank cell does not clear the field.',
    {
      column,
      value,
      context:
        'review grid validation warning: the cell contains only spaces or tabs, which is likely an error rather than a clear',
    },
  );
}
